import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { openDB } from "idb";

import Sidebar from "../../components/Dashboard/Sidebar";
import Header from "../../components/Dashboard/Header";
import Footer from "../../components/Dashboard/Footer";
import QualityDeltaTabs from "../../components/Dashboard/Visualize/QualityDeltaTabs";
import Piechart from "../../components/Dashboard/Charts/Piechart";
import Table from "../../components/Dashboard/Charts/Table";
import useCommitStats from "../../hooks/useCommitStats";
import useRecentCommits from "../../hooks/useRecentCommits";
import QualityDeltaStatCards from "../../components/Dashboard/Charts/QualityDeltaStatCards";

const QualityDelta = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(3);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // clean loading state (no overlay spinner)
  const [isFetching, setIsFetching] = useState(false);

  //Selected commit passed from QualityGate chart
  const [selectedCommitId, setSelectedCommitId] = useState("");

  const [projectId, setProjectId] = useState("");

  const { smellType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedSmellTypeFilter = smellType || "all";

  const projectName = localStorage.getItem("selectedProjectName") || "";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const { commits: recentCommits, loading: recentCommitsLoading } = useRecentCommits(projectId, 50);
  useCommitStats(projectId); // keep hook call (if it triggers other side effects), not used

  const [qualityDeltaResponse, setQualityDeltaResponse] = useState(null);
  const [backendData, setBackendData] = useState(null);

  // Derived data
  const pieChartData = qualityDeltaResponse?.all?.pie || [];
  const archDeltaPie = qualityDeltaResponse?.by_type?.architecture?.pie || [];
  const archDeltaTable = qualityDeltaResponse?.by_type?.architecture?.table || [];
  const designPieChartData = qualityDeltaResponse?.by_type?.design?.pie || [];
  const designTableData = qualityDeltaResponse?.by_type?.design?.table || [];
  const implementationPieChart = qualityDeltaResponse?.by_type?.implementation?.pie || [];
  const implementationTableData = qualityDeltaResponse?.by_type?.implementation?.table || [];
  const analysisSummary = qualityDeltaResponse?.by_type?.summary || [];

  const isLargeScreen = windowWidth >= 992;

  const NoDataAvailable = ({ title }) => (
    <div className="card p-3 shadow-sm text-center">
      <h5 className="mb-1">{title}</h5>
      <p className="text-muted mb-0">No data available. Please see your uploaded files.</p>
    </div>
  );

  const InlineLoading = ({ text }) => (
    <div className="alert alert-light border d-flex align-items-center gap-2 py-2 px-3 mb-3">
      <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      <span className="text-muted">{text}</span>
    </div>
  );

  useEffect(() => {
    document.title = "DCode | Adhoc code quality";
  }, []);

  // Init from navigation state + localStorage
  useEffect(() => {
    if (location.state?.selectedCommitId) setSelectedCommitId(location.state.selectedCommitId);

    const storedProjectId = localStorage.getItem("selectedProjectId");
    if (storedProjectId) setProjectId(storedProjectId);
  }, [location.state]);

  // IndexedDB
  const dbPromise = useMemo(
    () =>
      openDB("AnalysisDB", 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains("analysis")) {
            db.createObjectStore("analysis", { keyPath: "id" });
          }
        },
      }),
    []
  );

  // Fetch initial analyzed data from IndexedDB
  useEffect(() => {
    async function fetchData() {
      try {
        const db = await dbPromise;
        const storedData = await db.get("analysis", "analyzedData");

        const storedProjectId = localStorage.getItem("selectedProjectId");
        if (storedData && storedData.data) {
          setBackendData(storedData.data);
          if (!projectId && storedProjectId) setProjectId(storedProjectId);
        } else {
          console.error("No analyzed data found in IndexedDB. Redirecting...");
          navigate("/upload");
        }
      } catch (err) {
        console.error("Failed to retrieve data:", err);
        navigate("/upload");
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, dbPromise]);

  // Sidebar width
  const getSidebarWidth = () => {
    if (!sidebarOpen) return "0px";
    return sidebarCollapsed ? "80px" : "280px";
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/project/quality-delta-explorer")) {
      setActiveSidebarItem(3);
    }
  }, [location.pathname]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);
  const handleSidebarCollapseChange = (collapsed) => setSidebarCollapsed(collapsed);

  // Compare dropdown state
  const [compareCommitA, setCompareCommitA] = useState(""); // from
  const [compareCommitB, setCompareCommitB] = useState(""); // to

  // Helpers
  const getCommitId = (c) => c?.commit_id || c?.id || "";

  const findPreviousCommitId = (commitsList, selectedId) => {
    if (!Array.isArray(commitsList) || !selectedId) return null;

    // Assumption: commits are newest -> oldest
    const idx = commitsList.findIndex((c) => getCommitId(c) === selectedId);
    if (idx === -1) return null;

    const prev = commitsList[idx + 1];
    return prev ? getCommitId(prev) : null;
  };

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, options);
    const data = await res.json();
    return data;
  };

  // Main fetch logic
  useEffect(() => {
    if (!projectId) return;
    if (recentCommitsLoading) return;

    const run = async () => {
      try {
        setIsFetching(true);

        const base = BACKEND_URL;
        const fromQualityGate = !!location.state?.fromQualityGate;
        const toCommit = location.state?.selectedCommitId || "";

        // If coming from QualityGate: compare(prev -> selected)
        if (fromQualityGate && toCommit) {
          const fromCommit = findPreviousCommitId(recentCommits, toCommit);

          if (fromCommit) {
            const data = await fetchJson(
              `${base}/quality-delta/projects/${projectId}/compare/?from=${fromCommit}&to=${toCommit}`,
              { headers: { "Content-Type": "application/json" } }
            );

            if (!data?.message) {
              setQualityDeltaResponse(data);
              setCompareCommitA(fromCommit);
              setCompareCommitB(toCommit);
              return;
            }
          }
        }

        // Default
        const data = await fetchJson(`${base}/quality-delta/projects/${projectId}/default/`, {
          headers: { "Content-Type": "application/json" },
        });

        if (data?.message) return;

        setQualityDeltaResponse(data);
        setCompareCommitA(data?.meta?.from_commit || "");
        setCompareCommitB(data?.meta?.to_commit || "");
      } catch (e) {
        console.error("Error fetching quality delta:", e);
      } finally {
        setIsFetching(false);
      }
    };

    run();
  }, [projectId, BACKEND_URL, location.state, recentCommits, recentCommitsLoading]);

  // Dropdown commits list
  const commits = useMemo(() => {
    return (recentCommits || []).map((c) => ({
      id: c.commit_id || c.id,
      message: c.message || c.commit_message || "",
      date: c.created_at ? new Date(c.created_at).toLocaleString() : c.date || "",
    }));
  }, [recentCommits]);

  const commitLabel = (c) => {
    const short = (c?.id || "").substring(0, 7);
    const msg = c?.message ? c.message.slice(0, 40) : "";
    const dt = c?.date ? ` • ${c.date}` : "";
    return `${short}${msg ? " — " + msg : ""}${dt}`;
  };

  // Manual compare
  const onCompare = async () => {
    if (!compareCommitA || !compareCommitB || compareCommitA === compareCommitB) return;

    try {
      setIsFetching(true);
      const data = await fetchJson(
        `${BACKEND_URL}/quality-delta/projects/${projectId}/compare/?from=${compareCommitA}&to=${compareCommitB}`
      );

      if (data?.message) return;

      setQualityDeltaResponse(data);
    } catch (e) {
      console.error("Error comparing commits:", e);
    } finally {
      setIsFetching(false);
    }
  };

  const arcTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Details", key: "description" },
    { label: "Category", key: "category" },
  ];

  const designTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Class", key: "class" },
    { label: "Line No", key: "line" },
    { label: "Details", key: "details" },
    { label: "Category", key: "category" },
  ];

  const impTableColumns = [
    { label: "Smell", key: "smell" },
    { label: "Package", key: "package" },
    { label: "Class", key: "class" },
    { label: "Function", key: "function" },
    { label: "Line No", key: "line" },
    { label: "Details", key: "details" },
    { label: "Category", key: "category" },
  ];

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
          pageTitle="Quality delta explorer"
        />
      </div>

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={setActiveSidebarItem}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {/* Mobile overlay */}
      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      {/* Main */}
      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          transition: "margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid px-4">
          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <h1 className="display-9 fw-bold mb-2 text-gradient mt-3">Quality delta explorer</h1>

              {/* neat inline loading indicator */}
              {isFetching && <InlineLoading text="Fetching quality delta..." />}

              {projectId && (
                <div className="d-flex flex-column gap-1">
                  <small className="text-muted fs-6">
                    <strong>Project name: </strong> <span>{projectName} </span>{" "}
                    <strong className="header-small">Project ID:</strong> {projectId}

                    <span className="ms-3 d-inline-flex align-items-center gap-2">
                      <strong>Compare commits</strong>

                      <select
                        className="form-select form-select-sm"
                        style={{ width: 260 }}
                        value={compareCommitA}
                        onChange={(e) => setCompareCommitA(e.target.value)}
                      >
                        <option value="">
                          {recentCommitsLoading ? "Loading commits..." : "Select commit A"}
                        </option>
                        {commits.map((c) => (
                          <option key={c.id} value={c.id} disabled={!!compareCommitB && c.id === compareCommitB}>
                            {commitLabel(c)}
                          </option>
                        ))}
                      </select>

                      <select
                        className="form-select form-select-sm"
                        style={{ width: 260 }}
                        value={compareCommitB}
                        onChange={(e) => setCompareCommitB(e.target.value)}
                      >
                        <option value="">
                          {recentCommitsLoading ? "Loading commits..." : "Select commit B"}
                        </option>
                        {commits.map((c) => (
                          <option key={c.id} value={c.id} disabled={!!compareCommitA && c.id === compareCommitA}>
                            {commitLabel(c)}
                          </option>
                        ))}
                      </select>

                      <button
                        className="btn btn-primary"
                        disabled={
                          isFetching || !compareCommitA || !compareCommitB || compareCommitA === compareCommitB
                        }
                        onClick={onCompare}
                        title={compareCommitA === compareCommitB ? "Pick two different commits" : "Compare selected commits"}
                        style={{
                          backgroundColor: "rgb(117, 196, 192)",
                          color: "white",
                          border: "none",
                        }}
                      >
                        {isFetching ? "Comparing..." : "Compare"}
                      </button>
                    </span>
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-12">
              <QualityDeltaTabs />
            </div>
          </div>

          <div className="row g-4">
            {selectedSmellTypeFilter === "all" && (
              <div className="row mt-4">
                <div className="col-md-6">
                  {pieChartData.length > 0 ? (
                    <Piechart data={pieChartData} title="Distribution of smell types" />
                  ) : (
                    <NoDataAvailable title="Distribution of smell types" />
                  )}
                </div>
                <div className="col-md-6">
                  <QualityDeltaStatCards summary={analysisSummary} title="Analysis summary" />
                </div>
              </div>
            )}

            {selectedSmellTypeFilter === "architecture" && (
              <div className="row g-3">
                <div className="col-md-6">
                  {archDeltaPie.length > 0 ? (
                    <Piechart data={archDeltaPie} title="Architecture smell distribution by category" />
                  ) : (
                    <NoDataAvailable title="Architecture smell distribution by category" />
                  )}
                </div>
                <div className="col-md-6">
                  {archDeltaTable.length > 0 ? (
                    <Table
                      title="Architecture smells"
                      data={archDeltaTable}
                      columns={arcTableColumns}
                      searchKey="package"
                      enableRadioFilter={true}
                      radioField="category"
                      radioDefault="all"
                    />
                  ) : (
                    <NoDataAvailable title="Architecture smells" />
                  )}
                </div>
              </div>
            )}

            {selectedSmellTypeFilter === "implementation" && (
              <div className="row g-3">
                <div className="col-md-5">
                  {implementationPieChart.length > 0 ? (
                    <Piechart data={implementationPieChart} title="Implementation smell distribution by category" />
                  ) : (
                    <NoDataAvailable title="Implementation smell distribution by category" />
                  )}
                </div>
                <div className="col-md-7">
                  {implementationTableData.length > 0 ? (
                    <Table
                      title="Implementation smells"
                      data={implementationTableData}
                      columns={impTableColumns}
                      searchKey="function"
                      enableRadioFilter={true}
                      radioField="category"
                      radioDefault="all"
                    />
                  ) : (
                    <NoDataAvailable title="Implementation smells" />
                  )}
                </div>
              </div>
            )}

            {selectedSmellTypeFilter === "design" && (
              <div className="row g-3">
                <div className="col-md-5">
                  {designPieChartData.length > 0 ? (
                    <Piechart data={designPieChartData} title="Design smell distribution by category" />
                  ) : (
                    <NoDataAvailable title="Design smell distribution by category" />
                  )}
                </div>
                <div className="col-md-7">
                  {designTableData.length > 0 ? (
                    <Table
                      title="Design smells"
                      data={designTableData}
                      columns={designTableColumns}
                      searchKey="class"
                      enableRadioFilter={true}
                      radioField="category"
                      radioDefault="all"
                    />
                  ) : (
                    <NoDataAvailable title="Design smells" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default QualityDelta;
